import { useHomepageVisibility } from '../utils/homepageVisibility';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import axiosInstance from '../api/axios';
import useHairCollections from '../hooks/useHairCollections';

/**
 * Kollektionen — section with category tabs.
 *
 * - Fetches all active categories from the API.
 * - If only ONE active category (default: "limited-hair"), renders the classic
 *   single-section UI (no tabs) for backwards-compatible visuals.
 * - If 2+ categories exist, renders Tab navigation and only shows collections
 *   matching the active tab.
 */
const CollectionTile = ({ c, mobile = false }) => (
  <Link
    to={`/collections/${c.slug}`}
    className={
      mobile
        ? 'snap-start shrink-0 w-[60%] xs:w-[55%] aspect-[3/4] relative overflow-hidden'
        : 'group relative aspect-[4/3] overflow-hidden transition-transform hover:-translate-y-1'
    }
    style={{ backgroundColor: c.palette.bg, color: c.palette.text }}
    data-testid={`collection-tile${mobile ? '-mobile' : ''}-${c.slug}`}
  >
    <div className={`absolute inset-0 ${mobile ? 'p-4' : 'p-5 md:p-7'} flex flex-col`}>
      <div className={mobile ? 'text-2xl mb-0.5' : 'text-3xl md:text-4xl mb-1'}>{c.emoji}</div>
      <p
        className={`${mobile ? 'text-[9px] tracking-[2px]' : 'text-[10px] tracking-[3px]'} uppercase font-semibold opacity-80`}
        style={{ color: c.palette.accent }}
      >
        {c.style}
      </p>
      <h3
        className={`${mobile ? 'text-base mt-0.5' : 'text-xl md:text-2xl mt-1'} font-bold leading-tight`}
        style={{ fontFamily: 'Georgia, serif' }}
      >
        {c.name}
      </h3>
      <p className={`${mobile ? 'text-[10px] mt-1' : 'text-xs md:text-sm mt-2'} opacity-80 leading-relaxed ${mobile ? 'line-clamp-2' : ''}`}>
        {c.tagline}
      </p>

      {!mobile && (
        <div className="mt-auto flex flex-wrap gap-1.5">
          {c.colors.slice(0, 4).map((col) => (
            <span
              key={col}
              className="text-[10px] px-2 py-0.5"
              style={{
                backgroundColor: `${c.palette.accent}22`,
                color: c.palette.text,
                border: `1px solid ${c.palette.accent}55`,
              }}
            >
              {col}
            </span>
          ))}
        </div>
      )}

      <div
        className={
          mobile
            ? 'mt-auto inline-flex items-center gap-1 text-[9px] tracking-[2px] uppercase font-semibold'
            : 'absolute bottom-4 right-5 inline-flex items-center gap-1 text-[11px] tracking-[2px] uppercase font-semibold opacity-0 group-hover:opacity-100 transition-opacity'
        }
        style={{ color: c.palette.accent }}
      >
        {mobile ? 'Ansehen' : 'Entdecken'}
        <ArrowRight className={mobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      </div>
    </div>
  </Link>
);

const LimitedCollectionsSectionInner = () => {
  const { collections } = useHairCollections();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('limited-hair');

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/hair-collections/categories')
      .then((res) => {
        if (cancelled) return;
        const cats = Array.isArray(res.data) ? res.data.filter((c) => c.is_active !== false) : [];
        setCategories(cats);
        if (cats.length && !cats.find((c) => c.slug === activeCategory)) {
          setActiveCategory(cats[0].slug);
        }
      })
      .catch(() => { /* fall back to single section */ });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!collections || collections.length === 0) return [];
    if (categories.length <= 1) return collections;
    return collections.filter((c) => (c.category || 'limited-hair') === activeCategory);
  }, [collections, categories, activeCategory]);

  if (!collections || collections.length === 0) return null;

  const activeCat = categories.find((c) => c.slug === activeCategory);
  const showTabs = categories.length > 1;

  // Always show the generic "Limited Collections" banner on the teaser —
  // user wants the shop hint to remain generic (not switch to active category).
  const bannerTitle = 'Limited Collections';
  const bannerTagline = 'Vier Jahreszeiten · ein Lebensgefühl';
  const bannerDescription =
    'Kuratierte Boxen rund um Haar, Beauty, Home, Study & saisonale Highlights. Limitierte Stückzahlen — solange verfügbar.';

  return (
    <section
      className="py-6 md:py-16 px-3 sm:px-6 lg:px-8 bg-white"
      data-testid="limited-hair-collections"
    >
      <div className="max-w-7xl mx-auto">
        {/* Banner */}
        <div
          className="relative overflow-hidden mb-4 md:mb-10 px-4 md:px-12 py-5 md:py-14 text-center"
          style={{
            background: 'linear-gradient(135deg, #5D4E3C 0%, #6E5C48 100%)',
            color: '#F5EDE4',
          }}
        >
          <div className="absolute top-3 right-3 opacity-20">
            <Sparkles className="h-10 w-10 md:h-24 md:w-24" />
          </div>
          <p className="text-[9px] md:text-[11px] tracking-[3px] md:tracking-[5px] uppercase mb-1.5 md:mb-3" style={{ color: '#D4AF37' }}>
            ✨ Kollektionen
          </p>
          <h2 className="text-xl sm:text-3xl md:text-5xl font-bold mb-1 md:mb-3 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            {bannerTitle}
          </h2>
          <p className="text-[11px] sm:text-base md:text-lg italic md:mb-2 opacity-90">
            {bannerTagline}
          </p>
          {bannerDescription && (
            <p className="hidden md:block max-w-2xl mx-auto text-sm md:text-base mt-4 opacity-85 leading-relaxed">
              {bannerDescription}
            </p>
          )}
          <Link
            to="/collections"
            className="inline-flex items-center gap-1.5 mt-3 md:mt-6 px-4 md:px-8 py-1.5 md:py-3 text-[10px] md:text-xs tracking-[2px] md:tracking-[3px] uppercase font-semibold transition-colors"
            style={{ backgroundColor: '#F5EDE4', color: '#5D4E3C' }}
            data-testid="limited-collections-cta"
          >
            Kollektion entdecken
            <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
          </Link>
        </div>

        {/* Tab navigation (only when 2+ categories) */}
        {showTabs && (
          <div className="flex items-center justify-center gap-1 mb-6 border-b border-[#E8DCC8] overflow-x-auto" data-testid="collection-tabs">
            {categories.map((cat) => {
              const isActive = activeCategory === cat.slug;
              const count = collections.filter((c) => (c.category || 'limited-hair') === cat.slug).length;
              if (count === 0) return null;
              return (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`shrink-0 px-4 py-2.5 text-xs md:text-sm transition-all border-b-2 -mb-px flex items-center gap-2 ${
                    isActive
                      ? 'text-[#5D4E3C] font-semibold'
                      : 'border-transparent text-[#9C8C7A] hover:text-[#5D4E3C]'
                  }`}
                  style={isActive ? { borderBottomColor: cat.accent_color || '#5D4E3C' } : { borderBottomColor: 'transparent' }}
                  data-testid={`shop-category-tab-${cat.slug}`}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* MOBILE: horizontal-scroll snap row */}
        <div
          className="md:hidden flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
          style={{ scrollbarWidth: 'none' }}
        >
          {filtered.map((c) => <CollectionTile key={c.slug} c={c} mobile />)}
          <div className="shrink-0 w-1" aria-hidden="true" />
        </div>

        {/* DESKTOP: grid */}
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((c) => <CollectionTile key={c.slug} c={c} />)}
        </div>
      </div>
    </section>
  );
};

const LimitedCollectionsSection = () => {
  const visibility = useHomepageVisibility();
  return visibility.show_collections ? <LimitedCollectionsSectionInner /> : null;
};

export default LimitedCollectionsSection;
